import { motion } from 'framer-motion';
import type { ComponentType } from 'react';

import { DesktopFrame, PhoneFrame } from './DeviceFrame';

import {
  MockupCrise,
  MockupDistracao,
  MockupN1Home,
  MockupN2Home,
  MockupN3Home,
  MockupPainelMedico,
} from '@marketing/illustrations/MockupScreens';
import { fadeUp, revealOnView, stagger } from '@marketing/motion/variants';

// ============================================================
// ScreensShowcase — 6 mockups SVG fiéis das telas reais
// ============================================================
// Por que mockups SVG (~3-5 KB cada) e não screenshots PNG (~150 KB):
//   - Theme-aware (light/dark/HC) automaticamente via CSS vars
//   - Resilientes a mudanças de UI no app (não ficam desatualizados)
//   - Zero peso de imagem real (cabem no orçamento de bundle)
//   - Acessíveis via aria-label (impossível em screenshots)
//   - Vetoriais (escala perfeita em qualquer DPR)
// ============================================================

interface Screen {
  id: string;
  title: string;
  caption: string;
  alt: string;
  Mockup: ComponentType<{ ariaLabel: string }>;
  format: 'phone' | 'desktop';
}

const screens: ReadonlyArray<Screen> = [
  {
    id: 'n1',
    title: 'Autista Nível 1 (verbal autoadvogado)',
    caption:
      'Mood quick em escala 1-5, agenda visual com horários, atalho ao assistente. Densidade média, linguagem literal.',
    alt: 'Tela do autista N1 com saudação "Bom dia, Davi", card de humor com 5 emojis e agenda do dia.',
    Mockup: MockupN1Home,
    format: 'phone',
  },
  {
    id: 'n2',
    title: 'Autista Nível 2 (com pictogramas)',
    caption:
      'Pictogramas grandes para humor, atalhos visuais (Falar/Calma/Distrair) e agenda em cards ilustrados.',
    alt: 'Tela do autista N2 com pictogramas grandes de humor 1-5 e cards ilustrados para falar, calma e distração.',
    Mockup: MockupN2Home,
    format: 'phone',
  },
  {
    id: 'n3',
    title: 'Autista Nível 3 (CAA-first, low-stim)',
    caption:
      'CAA prioritária na tela inicial, escala de humor reduzida a 3 estados, pictogramas 130×130, low-stim.',
    alt: 'Tela do autista N3 com 3 estados de humor (TRISTE/TRANQUILO/FELIZ) e grade de pictogramas grandes para CAA.',
    Mockup: MockupN3Home,
    format: 'phone',
  },
  {
    id: 'painel',
    title: 'Painel médico — prontuário vivo',
    caption:
      'Lista priorizada à esquerda, sumário IA "hipótese—não diagnóstico", linha do tempo de eventos e indicador de fonte.',
    alt: 'Painel desktop do médico com lista de 5 pacientes na lateral, card de sumário IA e linha do tempo de eventos.',
    Mockup: MockupPainelMedico,
    format: 'desktop',
  },
  {
    id: 'distrair',
    title: 'Modo Distração (stim toy BubblePop)',
    caption:
      'Auto-regulação por estímulo sensorial. Bolhas reagem ao toque. Coleta features passivas (toques/min, duração).',
    alt: 'Tela escura do Modo Distração com bolhas coloridas espalhadas e contador discreto de sessão.',
    Mockup: MockupDistracao,
    format: 'phone',
  },
  {
    id: 'crise',
    title: 'Modo Crise (cuidador)',
    caption:
      'Tipografia 22-32pt, roteiro adaptado em 3 passos, botão SAMU 192 destacado, CVV 188 secundário.',
    alt: 'Tela do Modo Crise com banner de aviso, 3 passos numerados e botão grande vermelho de chamar SAMU 192.',
    Mockup: MockupCrise,
    format: 'phone',
  },
];

export function ScreensShowcase() {
  return (
    <section
      id="showcase"
      className="lume-section"
      aria-labelledby="showcase-title"
    >
      <div className="lume-container">
        <motion.header
          {...revealOnView}
          variants={stagger(0.08)}
          className="lume-stack lume-stack-md"
          style={{ marginBottom: 'clamp(2rem, 4vw, 3rem)', maxWidth: '50rem' }}
        >
          <motion.p variants={fadeUp} className="lume-eyebrow">Telas em produção</motion.p>
          <motion.h2 variants={fadeUp} id="showcase-title" className="lume-h2 lume-text-balance">
            A mesma codebase — três níveis de suporte, três experiências adaptadas
          </motion.h2>
          <motion.p variants={fadeUp} className="lume-body-lg lume-text-pretty">
            Disponíveis para compreensão, mockups vetoriais fiéis das telas reais já em produção.
            Conteúdo representa as 6 contas sintéticas-piloto (Davi N1, Helena N2, Miguel N3,
            Tiago N1, Sofia N2 + cuidador e médica). Theme-aware: trocam para o modo escuro com o
            resto da página.
          </motion.p>
        </motion.header>

        <motion.div
          {...revealOnView}
          variants={stagger(0.1)}
          className="lume-showcase"
        >
          {screens.map((s) => {
            const { Mockup } = s;
            return (
              <motion.figure
                key={s.id}
                variants={fadeUp}
                style={{ margin: 0, display: 'flex', flexDirection: 'column', gap: '1rem' }}
                whileHover={{ y: -4 }}
                transition={{ duration: 0.25, ease: [0.2, 0.8, 0.2, 1] }}
              >
                {s.format === 'phone' ? (
                  <PhoneFrame alt={s.alt}>
                    <Mockup ariaLabel={s.alt} />
                  </PhoneFrame>
                ) : (
                  <DesktopFrame alt={s.alt}>
                    <Mockup ariaLabel={s.alt} />
                  </DesktopFrame>
                )}
                <figcaption className="lume-stack lume-stack-sm">
                  <h3 className="lume-h3" style={{ fontSize: '1.05rem' }}>{s.title}</h3>
                  <p style={{ margin: 0, color: 'var(--color-fg-muted)', fontSize: '0.9rem' }}>
                    {s.caption}
                  </p>
                </figcaption>
              </motion.figure>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
